import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Heading,
  Icon,
  Pill,
  Stack,
  Table,
  TableColumn,
  Text,
  Tooltip,
  useNotifications,
} from '@sixfold/common-ui';
import { notNil } from '@sixfold/typed-primitives';
import copy from 'copy-to-clipboard';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router';

import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { useIsAdmin } from '../../lib/authorization';
import {
  DeleteOvdTokenMutation,
  DeleteOvdTokenMutationVariables,
  OvdTokenInfoFragment,
  OvdTokensQuery,
  OvdTokensQueryVariables,
  RevealOvdTokenMutation,
  RevealOvdTokenMutationVariables,
} from '../../lib/graphql';
import { Routes } from '../../routes';
import { Captcha } from '../../telematic_integration/components/captcha';
import { OvdTokenDeleteDialog } from '../components/ovd_token_delete_dialog';
import { deleteOvdTokenMutation, ovdTokensQuery, revealOvdTokenMutation } from '../graphql';

import styles from './styles.module.css';

type TokenRow = OvdTokenInfoFragment & { id: string; token?: never; action?: never };

function getEnabledFlags(token: OvdTokenInfoFragment): string[] {
  return [
    token.enableStatus && 'Status',
    token.enableApiEta && 'API ETA',
    token.enableCustomFields && 'Custom Fields',
    token.enableCancellationReason && 'Cancellation Reason',
    token.enableLocation && 'Location',
  ].filter((f): f is string => typeof f === 'string');
}

function renderIdPills(ids: string[]): React.ReactNode {
  if (ids.length === 0) {
    return <Text prominence="subtle">None</Text>;
  }
  return (
    <Stack direction="row" gap="xsmall" wrap>
      {ids.map((id) => (
        <Pill key={id}>{id}</Pill>
      ))}
    </Stack>
  );
}

const RevealOvdTokenButton: React.FC<{ tokenId: string }> = ({ tokenId }) => {
  const client = useApolloClient();
  const notify = useNotifications();
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [revealedToken, setRevealedToken] = useState<string | null>(null);

  const runQuery = async (captchaToken: string) => {
    try {
      const { data } = await client.mutate<RevealOvdTokenMutation, RevealOvdTokenMutationVariables>({
        mutation: revealOvdTokenMutation,
        variables: { tokenId, captchaToken },
      });
      const token = data?.revealOvdToken?.token;
      if (token) {
        setRevealedToken(token);
      } else {
        notify.error({ title: 'No token value returned' });
      }
    } catch (e) {
      console.error(e);
      notify.error({
        title: 'Failed to reveal token',
        message: e instanceof Error ? e.message : undefined,
      });
    }
    setShowCaptcha(false);
  };

  const handleCopy = async (token: string) => {
    const copied = await copy(token);
    if (copied) {
      notify.success({ title: 'Token copied to clipboard' });
    }
  };

  if (revealedToken !== null) {
    return (
      <Stack direction="row" gap="xsmall" alignItems="center">
        <Box className={styles.revealedToken}>{revealedToken}</Box>
        <Tooltip content="Copy token" placement="top">
          <Button kind="ghost" size="xsmall" onClick={() => void handleCopy(revealedToken)}>
            Copy
          </Button>
        </Tooltip>
      </Stack>
    );
  }

  return (
    <>
      {showCaptcha && (
        <Captcha
          onClickAway={() => setShowCaptcha(false)}
          onChange={(captcha_token) => captcha_token != null && runQuery(captcha_token)}
        />
      )}
      <Tooltip content="Reveal token value" placement="top">
        <Button onClick={() => setShowCaptcha(true)} size="xsmall" kind="subtle">
          <Icon name="small-visible" size={16} />
        </Button>
      </Tooltip>
    </>
  );
};

export const OvdTokenListContainer = () => {
  const isAdmin = useIsAdmin();
  const notify = useNotifications();
  const { data, loading } = useQuery<OvdTokensQuery, OvdTokensQueryVariables>(ovdTokensQuery);
  const [deleteToken, { loading: isDeleting }] = useMutation<DeleteOvdTokenMutation, DeleteOvdTokenMutationVariables>(
    deleteOvdTokenMutation,
  );
  const [tokenToDelete, setTokenToDelete] = useState<OvdTokenInfoFragment | null>(null);

  const tokens: TokenRow[] = useMemo(() => (data?.ovdTokens ?? []).map((t) => ({ ...t, id: t.tokenId })), [data]);

  const handleDelete = async () => {
    if (tokenToDelete === null) return;
    try {
      await deleteToken({
        variables: { input: { tokenId: tokenToDelete.tokenId } },
        refetchQueries: [{ query: ovdTokensQuery }],
      });
    } catch (e) {
      console.error(e);
      notify.error({
        title: 'Failed to delete OVD token',
        message: e instanceof Error ? e.message : undefined,
      });
      return;
    }
    notify.success({ title: 'OVD token deleted' });
    setTokenToDelete(null);
  };

  const columns = useMemo((): TableColumn<TokenRow>[] => {
    const base: TableColumn<TokenRow>[] = [
      {
        id: 'label',
        title: 'Label',
        cellContent: ({ row }) => (
          <Link to={Routes.OvdTokenDetail.generatePath({ token_id: row.tokenId })}>{row.label || '(no label)'}</Link>
        ),
      },
      {
        id: 'token',
        title: 'Token',
        cellContent: ({ row }) => (isAdmin ? <RevealOvdTokenButton tokenId={row.tokenId} /> : null),
      },
      {
        id: 'platformIds',
        title: 'Platform IDs',
        cellContent: ({ row }) => renderIdPills(row.platformIds),
      },
      {
        id: 'shipperIds',
        title: 'Shipper IDs',
        cellContent: ({ row }) => renderIdPills(row.shipperIds),
      },
      {
        id: 'carrierIds',
        title: 'Carrier IDs',
        cellContent: ({ row }) => renderIdPills(row.carrierIds),
      },
      {
        id: 'enableStatus',
        title: 'Flags',
        cellContent: ({ row }) => {
          const flags = getEnabledFlags(row);
          return (
            <Stack direction="row" gap="xsmall" wrap>
              {flags.length > 0 ? (
                flags.map((flag) => (
                  <Pill key={flag} kind="success">
                    {flag}
                  </Pill>
                ))
              ) : (
                <Text prominence="subtle">None</Text>
              )}
            </Stack>
          );
        },
      },
      {
        id: 'createdAt',
        title: 'Created',
        cellContent: ({ row }) => <FormattedDate date={row.createdAt} />,
      },
    ];

    if (isAdmin) {
      base.push({
        id: 'action',
        title: 'Actions',
        cellContent: ({ row }) => (
          <Stack direction="row" gap="xsmall">
            <Link to={Routes.OvdTokenDetail.generatePath({ token_id: row.tokenId })}>
              <Button kind="subtle" size="xsmall">
                Edit
              </Button>
            </Link>
            <Button kind="subtle" size="xsmall" onClick={() => setTokenToDelete(row)}>
              <Stack direction="row" gap="xsmall" alignItems="center">
                <Icon name="small-cross" size={16} /> Delete
              </Stack>
            </Button>
          </Stack>
        ),
      });
    }

    return base;
  }, [isAdmin]);

  if (loading) {
    return <Text>Loading...</Text>;
  }

  return (
    <Stack direction="column" gap="medium">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Heading size="medium">OVD Tokens ({tokens.length})</Heading>
        {isAdmin && (
          <Link to={Routes.OvdTokenCreate.generatePath({})}>
            <Button kind="primary">Create Token</Button>
          </Link>
        )}
      </Stack>

      {tokens.length === 0 ? <Text>No OVD tokens found.</Text> : <Table data={tokens} columns={columns} />}

      {notNil(tokenToDelete) && (
        <OvdTokenDeleteDialog
          tokenLabel={tokenToDelete.label ?? ''}
          isDeleting={isDeleting}
          onConfirm={handleDelete}
          onCancel={() => setTokenToDelete(null)}
        />
      )}
    </Stack>
  );
};
